// src/quality-management/hooks/useDirectory.ts
// Cache leve (TTL + dedupe de requisições em voo) para listas de funcionários e gerentes.
// Evita que cada abertura de modal refaça o mesmo fetch.

import { useEffect, useState } from 'react';
import { User } from '../services/api/types';
import { apiService } from '../services/api';

const TTL_MS = 60_000; // 1 minuto

interface CacheEntry {
  at: number;
  data: User[];
}

let employeesCache: CacheEntry | null = null;
let managersCache: CacheEntry | null = null;
let employeesInflight: Promise<User[]> | null = null;
let managersInflight: Promise<User[]> | null = null;

function isFresh(entry: CacheEntry | null): boolean {
  return !!entry && Date.now() - entry.at < TTL_MS;
}

function loadEmployees(): Promise<User[]> {
  if (isFresh(employeesCache)) return Promise.resolve(employeesCache!.data);
  if (employeesInflight) return employeesInflight;
  employeesInflight = apiService.users
    .getEmployees()
    .then((data) => {
      employeesCache = { at: Date.now(), data };
      return data;
    })
    .finally(() => {
      employeesInflight = null;
    });
  return employeesInflight;
}

function loadManagers(): Promise<User[]> {
  if (isFresh(managersCache)) return Promise.resolve(managersCache!.data);
  if (managersInflight) return managersInflight;
  managersInflight = apiService.users
    .getProjectManagers()
    .then((data) => {
      managersCache = { at: Date.now(), data };
      return data;
    })
    .finally(() => {
      managersInflight = null;
    });
  return managersInflight;
}

// Invalida o cache (ex.: após criar/editar usuários no admin)
export function invalidateDirectoryCache() {
  employeesCache = null;
  managersCache = null;
}

interface DirectoryState {
  data: User[];
  loading: boolean;
}

function useDirectory(loader: () => Promise<User[]>, fresh: () => boolean, current: () => User[]): DirectoryState {
  const [data, setData] = useState<User[]>(() => (fresh() ? current() : []));
  const [loading, setLoading] = useState<boolean>(() => !fresh());

  useEffect(() => {
    let active = true;
    if (fresh()) {
      setData(current());
      setLoading(false);
      return;
    }
    setLoading(true);
    loader()
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error('Directory load error:', e);
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading };
}

export function useEmployees(): DirectoryState {
  return useDirectory(loadEmployees, () => isFresh(employeesCache), () => employeesCache?.data || []);
}

export function useManagers(): DirectoryState {
  return useDirectory(loadManagers, () => isFresh(managersCache), () => managersCache?.data || []);
}
